/**
 * Renders the toast stack. Mounted once in pages/_app.tsx; raise toasts with useToast() or `toast` from
 * context/ToastContext.
 */
import { useToast, ToastType } from "@/context/ToastContext";
import { Icon } from "@iconify/react";
import { AnimatePresence, motion } from "framer-motion";

const STYLES: Record<ToastType, { box: string; icon: string }> = {
  error: { box: "bg-red-600 text-white", icon: "mdi:alert-circle-outline" },
  success: { box: "bg-green-700 text-white", icon: "mdi:check-circle-outline" },
  info: { box: "bg-gray-900 text-white dark:bg-gray-800", icon: "mdi:information-outline" },
};

const ToastContainer = () => {
  const { toasts, removeToast, pauseToast, resumeToast } = useToast();
  const polite = toasts.filter(t => t.type !== "error");
  const urgent = toasts.filter(t => t.type === "error");

  const render = (list: typeof toasts) => (
    <AnimatePresence initial={false}>
      {list.map(toast => {
        const s = STYLES[toast.type ?? "info"];
        return (
          <motion.div
            key={toast.id}
            layout
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            onMouseEnter={() => pauseToast(toast.id)}
            onMouseLeave={() => resumeToast(toast.id)}
            onFocus={() => pauseToast(toast.id)}
            onBlur={() => resumeToast(toast.id)}
            className={`group pointer-events-auto relative flex items-start gap-2.5 overflow-hidden rounded-card py-3 pl-3.5 pr-2 text-sm shadow-popover ${s.box}`}
          >
            <Icon icon={s.icon} className="mt-0.5 shrink-0 text-lg" aria-hidden="true" />
            <p className="min-w-0 flex-1 break-words leading-5">{toast.message}</p>
            <button
              type="button"
              onClick={() => removeToast(toast.id)}
              aria-label="Dismiss notification"
              className="-my-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-white/80 hover:bg-white/15 hover:text-white cursor-pointer focus-visible:outline-2 focus-visible:outline-white"
            >
              <Icon icon="mdi:close" className="text-base" aria-hidden="true" />
            </button>
            <div
              aria-hidden="true"
              className="animate-toast-bar absolute bottom-0 left-0 h-0.5 bg-white/40 group-hover:[animation-play-state:paused] group-focus-within:[animation-play-state:paused]"
              style={{ ["--toast-duration" as string]: `${toast.duration ?? 4000}ms` }}
            />
          </motion.div>
        );
      })}
    </AnimatePresence>
  );

  return (
    <div className="pointer-events-none fixed inset-x-4 bottom-4 z-[10050] flex flex-col gap-2 sm:inset-x-auto sm:bottom-6 sm:right-6 sm:w-[380px]">
      <div role="status" aria-live="polite" className="flex flex-col gap-2">
        {render(polite)}
      </div>
      <div role="alert" aria-live="assertive" className="flex flex-col gap-2">
        {render(urgent)}
      </div>
    </div>
  );
};

export default ToastContainer;
